import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { REACT_APP_SERVER_URL as base } from "../constants";
import { UserRole, matchRole } from "../types/status";

export interface User {
  id?: string;
  _id?: string;
  name: string;
  email: string;
  role: UserRole | string;
}

export interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (userData: any) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  getAuthHeaders: () => Record<string, string>;
  apiRequest: (url: string, options?: RequestInit) => Promise<any>;
  isAdmin: boolean;
  isRecruitmentAdmin: boolean;
  isStudentAdmin: boolean;
  isSuperAdmin: boolean;
  isEmployee: boolean;
  isApplicant: boolean;
  handleDownload: (url: string, filename?: string) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    // Load token and user from localStorage on app start
    const savedToken = localStorage.getItem("authToken");
    const savedUser = localStorage.getItem("userData");

    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
    }
    setIsLoading(false);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("userData");
    setToken(null);
    setUser(null);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await fetch(`${base}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      // If response is not OK, handle error
      if (!response.ok) {
        const data = await response.json();
        return {
          success: false,
          error: data.error || "Login failed due to some technical error",
        };
      }

      const data = await response.json();

      // Store token and user data
      localStorage.setItem("authToken", data.token);
      localStorage.setItem("userData", JSON.stringify(data.user));

      setToken(data.token);
      setUser(data.user);

      return { success: true };
    } catch (error: any) {
      console.error("Login error:", error);
      return { success: false, error: error.message };
    }
  };

  const register = async (userData: any) => {
    try {
      const response = await fetch(`${base}/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const data = await response.json();
        return {
          success: false,
          error:
            data.error || "Registration failed due to some technical error",
        };
      }

      const data = await response.json();

      // Store token and user data
      localStorage.setItem("authToken", data.token);
      localStorage.setItem("userData", JSON.stringify(data.user));

      setToken(data.token);
      setUser(data.user);

      return { success: true };
    } catch (error: any) {
      console.error("Registration error:", error);
      return { success: false, error: error.message };
    }
  };

  // Helper function to get authorization headers
  const getAuthHeaders = useCallback((): Record<string, string> => {
    if (token) {
      return {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };
    }
    return {
      "Content-Type": "application/json",
    };
  }, [token]);

  // Authenticated API request helper
  const apiRequest = useCallback(async (url: string, options: RequestInit = {}) => {
    try {
      const response = await fetch(`${base}${url}`, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...options.headers,
        } as Record<string, string>,
      });

      // Handle 401 specifically
      if (response.status === 401) {
        logout();
        throw new Error("Session expired. Please login again.");
      }

      // Try to parse JSON
      let data = null;
      const contentType = response.headers.get("content-type");

      if (contentType && contentType.includes("application/json")) {
        try {
          data = await response.json();
        } catch (err) {
          console.error("Failed to parse JSON response:", err);
          data = null;
        }
      }

      // Throw error for non-2xx responses
      if (!response.ok) {
        console.error("API Error:", {
          status: response.status,
          statusText: response.statusText,
          url,
          data,
        });

        // Build error message from various possible error formats
        let errorMessage = `Request failed with status ${response.status}`;

        if (data) {
          if (data.errors && Array.isArray(data.errors)) {
            errorMessage = data.errors
              .map((e: any) => e.msg || e.message || e)
              .join(", ");
          } else if (data.error) {
            errorMessage = data.error;
          } else if (data.message) {
            errorMessage = data.message;
          } else if (data?.error?.message) {
            errorMessage = data?.error?.message;
          }
        }

        const error: any = new Error(errorMessage);
        error.status = response.status;
        error.data = data;
        throw error;
      }

      return data;
    } catch (err: any) {
      // Re-throw if it's already our custom error
      if (err.status || err.message.includes("Session expired")) {
        throw err;
      }

      // Handle network errors or other fetch failures
      console.error("Network or unexpected error:", err);
      throw new Error(
        err.message || "Network error. Please check your connection."
      );
    }
  }, [getAuthHeaders, logout]);

  const handleDownload = useCallback(async (url: string, filename = "download.xlsx") => {
    try {
      const response = await fetch(`${base}${url}`, {
        method: "GET",
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (response.status === 401) {
        logout();
        throw new Error("Session expired. Please login again.");
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);

      return { success: true };
    } catch (error: any) {
      console.error("Download failed:", error);
      return { success: false, error: error.message };
    }
  }, [getAuthHeaders, logout]);

  const isSuperAdmin = matchRole(user?.role, UserRole.SUPER_ADMIN);
  const isRecruitmentAdmin = matchRole(user?.role, UserRole.RECRUITMENT_ADMIN) || isSuperAdmin;
  const isStudentAdmin = matchRole(user?.role, UserRole.STUDENT_ADMIN) || isSuperAdmin;
  const isAdmin = isRecruitmentAdmin || isStudentAdmin || isSuperAdmin;

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!token,
    isLoading,
    login,
    register,
    logout,
    getAuthHeaders,
    apiRequest,
    isAdmin,
    isRecruitmentAdmin,
    isStudentAdmin,
    isSuperAdmin,
    isEmployee: matchRole(user?.role, UserRole.EMPLOYEE),
    isApplicant: matchRole(user?.role, UserRole.APPLICANT),
    handleDownload,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default useAuth;
